import { List } from 'immutable';
import { RoomStaySummary } from '../models/room-stay-summary';
import { RoomTypeRateSummary, RoomTypeRateSummarySort } from '../models/room-type-rate-summary';
import { StaySummary } from '../models/stay-summary';
import { Transaction } from '../models/transaction';
import { TranslateService } from '@ngx-translate/core';
import { Reservation } from '../models/reservation';

export class TransactionUtils {

  /**
   * Transforms a list of transactions (Reservation or RSVP) into a RoomStaySummary object
   * Contains a stay summary and a room summary
   *
   * Immutable JS doc on groupBy
   * https://facebook.github.io/immutable-js/docs/#/List/groupBy
   *
   * @param transactions {List}
   * @param translateService {TranslateService}
   * @returns RoomStaySummary
   */
  static transformTransactionsToSummary<T extends Transaction>(transactions: List<T>,
                                                               translateService: TranslateService): RoomStaySummary {
    let roomTypeRateSummaryList = List<RoomTypeRateSummary>([]);

    // create lists of Reservations grouped by type and rate
    let listOfTransactionsGroupedByTypeAndRate = List<List<T>>([]);

    // this has to be re-created as a list or else groupBy is not available
    List(transactions).groupBy(roomTypeGroup => roomTypeGroup.roomType)
      .forEach(roomTypeGroup => {
        roomTypeGroup
          .groupBy(roomRateGroup => roomRateGroup.roomRate)
          .forEach(roomRateGroup => {
            listOfTransactionsGroupedByTypeAndRate = listOfTransactionsGroupedByTypeAndRate.push(roomRateGroup.toList());
          });
      });

    // iterate through each list to calculate aggregations (grouped by type and rate)
    let totalNights = 0;
    let totalRevenue = 0;
    let totalCommission = 0;
    listOfTransactionsGroupedByTypeAndRate.forEach((roomTypeAndRateGroup: List<T>) => {
      const roomTypeRateSummary: RoomTypeRateSummary =
        TransactionUtils.transformTransactionListToRoomSummary(roomTypeAndRateGroup, translateService);

      // sum total nights and total revenue
      totalNights = totalNights + roomTypeRateSummary.totalNights;
      totalRevenue = totalRevenue + roomTypeRateSummary.totalRevenue;
      totalCommission = totalCommission + roomTypeRateSummary.totalCommission;

      // adding room type and rate summary to list
      roomTypeRateSummaryList = roomTypeRateSummaryList.push(roomTypeRateSummary);
    });

    // Calculate avgRoomRate: totalRevenue / totalNights
    let avgRoomRate = 0;
    if (totalNights > 0) {
      avgRoomRate = totalRevenue / totalNights;
    }

    // Generate a room summary
    const staySummary = new StaySummary({
      totalNights: totalNights,
      totalRevenue: totalRevenue,
      avgRoomRate: avgRoomRate,
      totalItems: transactions.size,
      totalCommission: totalCommission
    });

    // build the room stay Summary
    return new RoomStaySummary({
      roomTypeRateSummaryList: roomTypeRateSummaryList.sort(RoomTypeRateSummarySort).toList(),
      staySummary: staySummary
    });
  }

  /**
   * Transforms a list of transactions into a RoomTypeRateSummary
   *
   * @param roomList {List}
   * @param translateService {TranslateService}
   * @returns RoomTypeRateSummary
   */
  static transformTransactionListToRoomSummary<T extends Transaction>(roomList: List<T>,
                                                                      translateService: TranslateService): RoomTypeRateSummary {
    let roomTypeRateSummary = new RoomTypeRateSummary();

    roomList.forEach((room: T) => {
      roomTypeRateSummary = new RoomTypeRateSummary({
        roomType: room.roomType ? room.roomType : translateService.instant('EVENT.OVERVIEW_CARDS.UNKNOWN_ROOM_TYPE'),
        roomRate: room.roomRate,
        // Note: this is called totalNights but it is actually "total room nights"
        totalNights: roomTypeRateSummary.totalNights + (room.nights * this.getRooms(room)),
        totalRevenue: roomTypeRateSummary.totalRevenue + (room.roomRate * room.nights * Math.max(1, this.getRooms(room))),
        totalCommission: roomTypeRateSummary.totalCommission + (room.payViaGp ? room.commission : 0)
      });
    });

    return roomTypeRateSummary;
  }

  /**
   * Returns the number of rooms
   * @param room
   */
  static getRooms(room: Transaction) {
    let rooms = 1;
    if (this.isReservation(room)) {
      rooms = (room as Reservation).rooms;
    }

    return rooms;
  }

  /**
   * Returns `true` if the room is of type Reservation and has the `rooms` property; `false` otherwise
   * @param {any} room
   */
  static isReservation(room: Transaction): room is Reservation {
    return (room as Reservation).rooms !== undefined;
  }

}



// WEBPACK FOOTER //
// /var/lib/jenkins/jobs/GroupPay/jobs/QA2/jobs/QA-Pioneer-ALL-2-POC/workspace/pioneer-web/src/app/event/utils/transaction-utils.ts